/**
 * Business Plan Q&A Page - Gap-filling questions for business plan generation
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import businessPlanService, { BusinessPlanQuestion } from '../services/businessPlanService';

const BusinessPlanQAPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [currentQuestion, setCurrentQuestion] = useState<BusinessPlanQuestion | null>(null);
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const startQA = async (retryCount = 0) => {
    try {
      setError(null);
      setLoading(true);
      await businessPlanService.startQA(sessionId!);
      setStarted(true);
      await loadCurrentQuestion();
    } catch (err: any) {
      if (retryCount < 1) {
        // Retry once — handles Render cold start timeouts
        await startQA(retryCount + 1);
        return;
      }
      setError(err.response?.data?.detail || 'Failed to start business plan Q&A');
      setLoading(false);
    }
  };

  const loadCurrentQuestion = async () => {
    try {
      const question = await businessPlanService.getCurrentQuestion(sessionId!);

      if (question.all_complete) {
        await generatePlan();
        return;
      }

      setCurrentQuestion(question);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load question');
      setLoading(false);
    }
  };

  const generatePlan = async () => {
    setGenerating(true);
    try {
      await businessPlanService.generate(sessionId!);
      navigate(`/business-plan/${sessionId}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate business plan');
      setGenerating(false);
    }
  };

  useEffect(() => {
    // Try to resume existing Q&A first; only start fresh if no questions exist yet
    const init = async () => {
      try {
        const question = await businessPlanService.getCurrentQuestion(sessionId!);
        if (question.all_complete && question.total_questions > 0) {
          // All questions answered — generate the plan
          await generatePlan();
        } else if (question.question) {
          // Resume in-progress Q&A
          setCurrentQuestion(question);
          setStarted(true);
          setLoading(false);
        } else {
          // No questions yet — start fresh
          await startQA();
        }
      } catch {
        // No Q&A started yet — start fresh
        await startQA();
      }
    };
    init();
  }, [sessionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!answer.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await businessPlanService.submitAnswer(sessionId!, answer.trim());
      setAnswer('');

      if (response.all_complete) {
        await generatePlan();
      } else {
        await loadCurrentQuestion();
      }

      setIsSubmitting(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit answer');
      setIsSubmitting(false);
    }
  };

  if (generating) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{
          padding: '40px',
          backgroundColor: '#e3f2fd',
          border: '2px solid #2196f3',
          borderRadius: '8px',
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '16px' }}>Generating Business Plan...</h2>
          <p style={{ fontSize: '1.1rem', color: '#666' }}>
            Creating your professional business plan based on all the information gathered. This may take a moment.
          </p>
          <Loader2 size={40} style={{ marginTop: '24px', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Preparing business plan questions...</h2>
      </div>
    );
  }

  if (error && !currentQuestion) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
        }}>
          <h3>Error</h3>
          <p>{error}</p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              onClick={() => startQA()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => navigate(`/reports/${sessionId}`)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Back to Reports
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Progress Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 style={{ fontSize: '2rem', margin: 0 }}>Business Plan — Additional Information</h1>
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#6f42c1',
            color: 'white',
            borderRadius: '20px',
            fontSize: '0.9rem',
            fontWeight: 'bold',
          }}>
            Question {currentQuestion?.question_number} of {currentQuestion?.total_questions}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ backgroundColor: '#e9ecef', borderRadius: '4px', height: '8px' }}>
          <div style={{
            backgroundColor: '#6f42c1',
            borderRadius: '4px',
            height: '8px',
            width: `${((currentQuestion?.question_number || 0) / (currentQuestion?.total_questions || 1)) * 100}%`,
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* Question */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          padding: '24px',
          backgroundColor: '#f8f9fa',
          border: '2px solid #dee2e6',
          borderRadius: '8px',
        }}>
          <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '8px', fontWeight: 'bold' }}>
            QUESTION
          </div>
          <p style={{ fontSize: '1.2rem', margin: 0, lineHeight: '1.6' }}>
            {currentQuestion?.question}
          </p>
        </div>
      </div>

      {/* Answer Form */}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', fontSize: '1.1rem' }}>
            Your Answer
          </label>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Provide details to help create a comprehensive business plan..."
            style={{
              width: '100%',
              minHeight: '180px',
              padding: '16px',
              fontSize: '1rem',
              border: '2px solid #ced4da',
              borderRadius: '4px',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !answer.trim()}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '1.1rem',
            backgroundColor: isSubmitting || !answer.trim() ? '#ccc' : '#6f42c1',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isSubmitting || !answer.trim() ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Answer & Continue'}
        </button>
      </form>
    </div>
  );
};

export default BusinessPlanQAPage;
